/* istanbul ignore file */
import { JsonPipe } from '@angular/common';
import { Component, Signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CustomerAutocompleteComponent } from '@libs/account/components';
import { ButtonComponent } from '@libs/common/components';
import { AuthService, UserAuthService } from '@libs/shared/auth';
import { FlashMessageModule, FlashMessageService } from '@libs/shared/common';
import { CustomerModel } from '@libs/account/models';
import { AuthToken } from '@libs/shared/models';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'ui-cep-login-debugger',
  templateUrl: './login-debugger.component.html',
  styleUrls: ['./login-debugger.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FlashMessageModule,
    CustomerAutocompleteComponent,
    JsonPipe,
    ButtonComponent,
  ],
})
export class LoginDebuggerComponent {
  public json: unknown;

  public userToken: Signal<AuthToken | undefined> = toSignal(this.userAuthService.readToken());
  public customers: Signal<CustomerModel[] | undefined> = this.authService.allCustomers;
  public customer: Signal<CustomerModel | undefined> = this.authService.currentCustomer;

  public constructor(
    private readonly authService: AuthService,
    private readonly flashMessage: FlashMessageService,
    private readonly userAuthService: UserAuthService,
    private readonly flashMessageService: FlashMessageService,
  ) {
  }

  public login(): void {
    void firstValueFrom(this.authService.login());
  }

  public logout(): void {
    if (this.userToken()) {
      void firstValueFrom(this.authService.logout());
    }
  }

  public async refreshIdToken(): Promise<void> {
    const freshToken: AuthToken | undefined = await firstValueFrom(this.userAuthService.refreshToken());

    this.json = { freshToken };
  }

  public async setExpiresUserAuth(): Promise<void> {
    const token: AuthToken | undefined = this.userToken();

    if (!token) {
      return;
    }

    await firstValueFrom(this.userAuthService.updateToken({
      ...token,
      expires: DateTime.fromMillis(0).toISO() ?? '',
    }));
  }

  public customerSelected(customer: CustomerModel | undefined): void {
    if (!customer || customer === this.customer()) {
      return;
    }

    void this.authService.changeCustomer(customer)
      .then(() => void this.flashMessage.showMessage('successLogin'))
      .catch(() => void this.flashMessage.showMessage('errorLogin'));
  }

  public async copyUserToken(): Promise<void> {
    await navigator.clipboard.writeText(this.userToken()?.bearer ?? '');
    this.flashMessageService.showCustomMessage({
      message: 'copied to clipboard!',
      theme: 'success',
    });
  }

  public async copyUserTokenForHttpConfig(): Promise<void> {
    const userToken: string = this.userToken()?.bearer ?? '';
    const json: string = `"azure_token":"${userToken}",\n`;

    await navigator.clipboard.writeText(json);
    this.flashMessageService.showCustomMessage({
      message: 'copied to clipboard!',
      theme: 'success',
    });
  }
}
